import * as React from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import type { ContentRef } from '@gitbook/api-client';
import type { InlineLinkData } from '@gitbook/doc-core';
import type { InlineProps } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { Button, Form, Popover, PopoverRoot, PopoverTrigger, Submit } from '@gitbook/spine-react';

import {
    ContentReferenceInputField,
    type ContentReferenceInputFieldProps,
} from '../../ContentReferences';
import { InlineCard, type InlineCardProps } from './InlineCard';
import {
    InlineContentRefPreviewCard,
    type InlineContentRefPreviewCardProps,
} from './InlineContentRefPreview';
import { InlineEditingContext } from './InlineEditingContext';
import { getInlineText, InlineTextField, setInlineText } from './InlineText';
import { useInlineEdit } from './useInlineEdit';
import { useIsInlineActiveBySelection } from './useIsInlineActive';

/**
 * Popover to edit an inline content ref.
 */
export function InlineContentRefEditPopover(
    props: Pick<InlineProps, 'editor' | 'node' | 'isSelected'> &
        Pick<InlineContentRefFormProps, 'accept'> &
        Pick<InlineCardProps, 'label'> &
        Omit<InlineContentRefPreviewCardProps, 'actions'> & {
            children: React.ReactElement;
        }
) {
    const { children, editor, node, accept, label, ...rest } = props;
    const isActiveBySelection = useIsInlineActiveBySelection(props);
    const [isManuallyOpen, setIsOpen] = React.useState(false);
    const [isEditing, setIsEditing] = React.useState(false);
    const isOpen = isManuallyOpen || isActiveBySelection;
    const inlineEdit = useInlineEdit({
        editor,
        node,
        isOpen,
        isEditing,
        onOpenChange: setIsOpen,
        onEdit: (isEditing) => {
            setIsEditing(isEditing);
        },
    });

    return (
        <PopoverRoot {...inlineEdit.popoverRootProps}>
            <InlineEditingContext.Provider value={isEditing && isOpen}>
                <PopoverTrigger>{children}</PopoverTrigger>
            </InlineEditingContext.Provider>
            <Popover {...inlineEdit.popoverProps}>
                {isEditing ? (
                    <InlineCard className="flex flex-col gap-3" label={`${label} edition`}>
                        <InlineContentRefForm
                            node={node}
                            editor={editor}
                            onSubmit={inlineEdit.submit}
                            accept={accept}
                        />
                    </InlineCard>
                ) : (
                    <InlineContentRefPreviewCard
                        {...rest}
                        label={label}
                        actions={
                            <>
                                <Button
                                    size="small"
                                    kind="blank"
                                    iconOnly
                                    leadingIcon={Icon.Edit}
                                    label="Edit"
                                    onPress={inlineEdit.edit}
                                />
                                <Button
                                    size="small"
                                    kind="blank"
                                    iconOnly
                                    leadingIcon={Icon.ClearCircle}
                                    label="Remove"
                                    onPress={() => {
                                        editor.change((change) => {
                                            change.unwrapInlineByKey(node.key);
                                        });
                                    }}
                                />
                            </>
                        }
                    />
                )}
            </Popover>
        </PopoverRoot>
    );
}

type InlineContentRefFormValues = {
    text: string;
    contentRef: ContentRef;
};

type InlineContentRefFormProps = Pick<InlineProps, 'editor' | 'node'> &
    Pick<ContentReferenceInputFieldProps, 'accept'> & { onSubmit: () => void };

/**
 * Form rendered when inline link is in edit mode.
 */
function InlineContentRefForm(props: InlineContentRefFormProps) {
    const { editor, node, accept } = props;
    const data = node.data.toFrozenObject<InlineLinkData>();
    const form = useForm<InlineContentRefFormValues>({
        defaultValues: { text: node.isVoid ? '' : getInlineText(node), contentRef: data.ref },
    });
    const onSubmit: SubmitHandler<InlineContentRefFormValues> = ({ text, contentRef }) => {
        editor.change((change) => {
            setInlineText(change, node, text);
            change.setDataByKey(node.key, ['ref'], contentRef);
        });
        props.onSubmit();
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            {node.isVoid ? null : <InlineTextField name="text" />}
            <ContentReferenceInputField
                name="contentRef"
                control={form.control}
                accept={accept}
                required
                autoFocus
                document={editor.value.document}
            />
            <Submit
                className="self-end"
                kind="blank"
                leadingIcon={Icon.CheckCircle}
                label="Apply"
                size="small"
                disabled={false}
            />
        </Form>
    );
}
